import type { Address, PersistedAddress } from "../api/address";
import { NoContactInstructions } from "../constants/address";
import { type SearchAddressPrediction } from "../api/address";
import * as AddressAPI from "../apiLoader/address";
import { z } from "zod";
import { processIssues } from "./validation_utils";
export const getDeliveryInstructions = (address: PersistedAddress) => {
  return address?.note?.replace(NoContactInstructions, "");
};

export const getNoContact = (address: PersistedAddress): boolean => {
  return address?.note?.startsWith(NoContactInstructions) || false;
};

export const buildDeliveryInstructions = (instruction: string, noContact: boolean) => {
  return noContact ? `${NoContactInstructions}${instruction}` : instruction;
};

export const getFormattedAddress = (address: Address) => {
  if (!address) return "";
  return `${address.address1}, ${address.city}, ${address.state}, ${address.zip}, ${address.country}`;
};

export const getUserAddressPredictions = async (query: string, countries: string[])
  : Promise<SearchAddressPrediction[]> => {
  const response = await AddressAPI.searchAddress(query, countries.join(",").toUpperCase());
  return response.addresses;
};

export const isAddressStreetNumberRequired = (address: string, countries: string[]) => {
  const onlyUSOrCA = countries.every(country => country === "us" || country === "ca");

  return onlyUSOrCA && !/^\d/.test(address.trim());
};

const getFieldValidators = (copy: Record<string, string>) => {
  return {
    address1: z
      .string()
      .trim()
      .min(1, { message: copy.address1Empty })
      .min(2, { message: copy.address1Invalid }),
    city: z
      .string()
      .trim()
      .min(1, { message: copy.cityEmpty })
      .min(2, { message: copy.cityInvalid }),
    state: z
      .string()
      .trim()
      .min(1, { message: copy.stateEmpty })
      .min(2, { message: copy.stateInvalid }),
    zip: z
      .string()
      .trim()
      .min(1, { message: copy.zipEmpty })
      .min(2, { message: copy.zipInvalid }),
    country: z
      .string()
      .trim()
      .min(1, { message: copy.countryEmpty })
      .min(2, { message: copy.countryInvalid })
  };
};

export const AddressValidator = {
  validateAllFields: (
    address: Address,
    copy: Record<string, string>
  ) => {
    const addressSchema = z.object(getFieldValidators(copy));
    const { success, data, error } = addressSchema.safeParse(address);
    return { success, data, issues: error ? processIssues(error.issues) : {} };
  }
};
